import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { TranscribeDialog } from '@/features/voice-library/components/TranscribeDialog';
import { usePermission } from '@/lib/permissions/use-permission';
import { cn } from '@/lib/utils/cn';
import { useState } from 'react';
import type { LucideIcon } from 'lucide-react';
import {
  ChevronDown,
 FileAudio,
  Languages,
  Loader2,
  Pause,
  PhoneIncoming,
  PhoneOff,
  Play,
  PlayCircle,
  Square,
  StopCircle,
  Type,
  Variable as VariableIcon,
} from 'lucide-react';
import { useTtsPreview } from '../../hooks/useTtsPreview';
import type { TtsFlowFormValues, TtsRow } from '../../types';

interface Props {
  values: TtsFlowFormValues;
  /** Editor uses this to update text-row preview caches when steps get
   *  synthesized through this pane. */
  onPatchRow: (index: number, patch: Partial<TtsRow>) => void;
  /** Flow owner's id — admin previews synthesize against the owner, not the viewer. */
  ownerUserId?: number;
}

type StepMode = 'prompt' | 'text' | 'variable';

const STEP_VISUAL: Record<
  StepMode,
  { bg: string; ring: string; icon: LucideIcon; label: string }
> = {
  prompt: {
    bg: 'bg-sky-500',
    ring: 'ring-sky-300',
    icon: FileAudio,
    label: 'Prompt',
  },
  text: {
    bg: 'bg-fuchsia-500',
    ring: 'ring-fuchsia-300',
    icon: Type,
    label: 'Text',
  },
  variable: {
    bg: 'bg-violet-500',
    ring: 'ring-violet-300',
    icon: VariableIcon,
    label: 'Variable',
  },
};

/** Hindi / English flows are readable as-is; any other language gets a transcript option. */
function needsTranscript(language?: string) {
  const base = (language ?? '').split('-')[0]?.toLowerCase();
  return Boolean(base) && base !== 'en' && base !== 'hi';
}

export function TtsFlowPreviewPane({ values, onPatchRow, ownerUserId }: Props) {
  const canTranscribe = usePermission('Transcription');
  const showTranscripts = canTranscribe && needsTranscript(values.language);
  const [transcribeTarget, setTranscribeTarget] = useState<{
    promptId: number;
    fileName: string;
    promptUrl?: string;
  } | null>(null);
  const {
    steps,
    playingIndex,
    busy,
    synthesizingIndex,
    handlePlayStep,
    handlePlayAll,
  } = useTtsPreview({
    rows: values.ttsRows,
    language: values.language,
    gender: values.gender,
    ownerUserId,
    onPatchRow,
  });

  const playAllActive = busy === 'playAll';

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between gap-2">
          <CardTitle className="flex items-center gap-1.5 text-sm">
            <PhoneIncoming className="h-3.5 w-3.5 text-muted-foreground" />
            Caller flow
          </CardTitle>
          <Button
            type="button"
            size="sm"
            variant={playAllActive ? 'destructive' : 'default'}
            onClick={handlePlayAll}
            disabled={steps.length === 0}
          >
            {playAllActive ? (
              <StopCircle className="h-3.5 w-3.5" />
            ) : (
              <PlayCircle className="h-3.5 w-3.5" />
            )}
            {playAllActive ? 'Stop' : 'Play all'}
          </Button>
        </div>
        <p className="text-xs text-muted-foreground">
          What the caller hears, step by step. Click any step's ▶ to preview it
          alone.
        </p>
      </CardHeader>
      <CardContent>
        {steps.length === 0 ? (
          <div className="rounded-md border border-dashed border-border bg-card/50 py-6 text-center text-xs text-muted-foreground">
            Add a step on the left to see the caller flow here.
          </div>
        ) : (
          <ol className="relative">
            {/* Vertical connector line behind the nodes */}
            <span
              aria-hidden
              className="pointer-events-none absolute left-[19px] top-2 bottom-2 w-px bg-border"
            />

            {/* Entry chip */}
            <li className="relative mb-3 flex items-center gap-3">
              <span className="z-10 flex h-10 w-10 items-center justify-center rounded-full border-2 border-border bg-card text-muted-foreground">
                <PhoneIncoming className="h-4 w-4" />
              </span>
              <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                Call starts
              </div>
            </li>

            {steps.map((s) => {
              const visual = STEP_VISUAL[s.mode];
              const Icon = visual.icon;
              const isPlaying = playingIndex === s.index && busy != null;
              const isSynth = synthesizingIndex === s.index;
              return (
                <li
                  key={s.index}
                  className="relative mb-3 flex items-start gap-3"
                >
                  {/* Node */}
                  <button
                    type="button"
                    onClick={() => handlePlayStep(s)}
                    disabled={!s.playable}
                    className={cn(
                      'group/node z-10 flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-white shadow-sm transition-all',
                      visual.bg,
                      isPlaying && 'ring-4',
                      isPlaying && visual.ring,
                      !s.playable && 'cursor-not-allowed opacity-60',
                      s.playable && 'hover:scale-105',
                    )}
                    aria-label={isPlaying ? 'Stop step' : 'Play step'}
                    title={isPlaying ? 'Stop' : 'Play this step'}
                  >
                    {isSynth ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : isPlaying ? (
                      busy === 'playAll' ? (
                        <Pause className="h-4 w-4" />
                      ) : (
                        <Square className="h-3 w-3" />
                      )
                    ) : (
                      <Icon className="h-4 w-4 group-hover/node:hidden" />
                    )}
                    {!isPlaying && !isSynth && (
                      <Play className="hidden h-4 w-4 group-hover/node:block" />
                    )}
                  </button>

                  {/* Card body */}
                  <div
                    className={cn(
                      'min-w-0 flex-1 rounded-md border bg-card px-2.5 py-1.5 transition-colors',
                      isPlaying
                        ? 'border-primary ring-1 ring-primary/40'
                        : 'border-border',
                    )}
                  >
                    <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                      <span>Step {s.index + 1}</span>
                      <span>·</span>
                      <span className={STEP_VISUAL[s.mode].bg.replace('bg-', 'text-')}>
                        {visual.label}
                      </span>
                    </div>
                    <div
                      className={cn(
                        'truncate text-xs font-medium text-foreground',
                        s.label.startsWith('(') && 'text-muted-foreground italic',
                      )}
                      title={s.label}
                    >
                      {s.label}
                    </div>
                    {s.detail && (
                      <div className="truncate text-[10px] text-muted-foreground">
                      {s.detail}
                      </div>
                    )}
                    {showTranscripts && s.mode === 'prompt' && s.playable && (
                      <button
                        type="button"
                        onClick={() =>
                          setTranscribeTarget({
                            promptId: Number(values.ttsRows[s.index]?.value),
                            fileName: s.label,
                            promptUrl: s.url,
                          })
                        }
                    className="mt-1.5 inline-flex items-center gap-1.5 rounded-full border border-violet-300/60 bg-gradient-to-r from-sky-500/10 to-violet-500/10 px-2.5 py-1 text-[11px] font-semibold text-violet-700 shadow-sm transition-all hover:-translate-y-px hover:border-violet-400 hover:shadow dark:text-violet-300"
                      >
                        <Languages className="h-3 w-3" />
                        Transcript
                      </button>
                    )}
                  </div>

                  {/* Down arrow tucked between nodes (visual flow cue) */}
                  <ChevronDown
                    aria-hidden
                    className="pointer-events-none absolute -bottom-3 left-[14px] h-3 w-3 text-border"
                  />
                </li>
              );
            })}

            {/* Hangup chip — implicit end of every flow */}
            <li className="relative mt-1 flex items-center gap-3">
              <span className="z-10 flex h-10 w-10 items-center justify-center rounded-full border-2 border-border bg-card text-muted-foreground">
                <PhoneOff className="h-4 w-4" />
              </span>
              <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                Call ends
              </div>
            </li>
          </ol>
        )}
      </CardContent>
      <TranscribeDialog
        prompt={transcribeTarget}
        onOpenChange={(o) => !o && setTranscribeTarget(null)}
      />
    </Card>
  );
}
